import { FormattedMessage, useIntl } from 'react-intl';

import { Modal, Form, Button } from 'antd';

import PartReportForm from '../PartReportForm';
import { ModalProps } from '@/types/ModalProps';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useReportGenerate } from '@/hooks';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { MailOutlined } from '@ant-design/icons';
import { QuestResponseType } from '@/core/domain/questReport/Reports/Quotation/Quotation.types';
import { useGeneratePartsReport } from '@/core/domain/reports/stock/Report.hooks';
import {
  ReportPartsParams,
  StockPartsFormValues,
} from '@/core/domain/reports/stock/Report.types';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

function PartReportModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();
  const [form] = Form.useForm<StockPartsFormValues>();

  const mutation = useGeneratePartsReport();
  const { fileLoading, setFileLoading, setFileUnloading } = useFileLoading();
  const report = useReportGenerate();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const values = await form.validateFields();

      const obj: ReportPartsParams = {
        ...values,
        format:
          format === 'CSV' ? QuestResponseType.CSV : QuestResponseType.PDF,
        companiesIds: values.companiesIds,
        subGroupIds: values.subGroupIds,
        manufacturerIds: values.manufacturerIds,
      };

      const result = (await mutation.mutateAsync(obj)) as ArrayBuffer;

      if (result.byteLength === 0) {
        return Modal.success({
          title: 'Relatório em processamento',
          content: (
            <>
              O link para o relatório de peças será enviado em breve no seu
              email <MailOutlined />
            </>
          ),
          onOk: () => handleVisible(),
          onCancel: () => handleVisible(),
        });
      }

      if (callback) {
        callback(result);
      }

      handleVisible();

      form.resetFields();
    } catch {}
    setFileUnloading(format);
  };

  return (
    <Modal
      width={750}
      open={visible}
      onCancel={handleVisible}
      title={intl.formatMessage({ id: 'customers.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="flex-end">
          <Button
            block
            type="primary"
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'peças.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
            key="viewReport"
          >
            <FormattedMessage id="customers.modal.report.buttons.viewReport" />
          </Button>

          <Button
            block
            type="primary"
            key="CSV"
            onClick={() =>
              handleGenerateReport('CSV', (result) =>
                report.downloadFile({ result, fileName: 'peças.csv' }),
              )
            }
          >
            Gerar CSV
          </Button>

          <Button
            block
            type="primary"
            key="PDF"
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({ result, fileName: 'peças.pdf' }),
              )
            }
          >
            <FormattedMessage id="customers.modal.report.buttons.pdf" />
          </Button>
        </SpaceForButton>
      }
    >
      {(fileLoading.REPORT_VIEW || fileLoading.CSV || fileLoading.PDF) && (
        <ModalLoadingOverlay messages={['Gerando relatório...']} />
      )}

      <PartReportForm form={form} />
    </Modal>
  );
}

export default PartReportModal;
